import React, { useState } from 'react';
import cashReservesService from '../../services/cashReservesService';

// ─── one-click apply for machine-actionable suggestions ───────────────────────
// Some alerts (e.g. "Note Monthly Withdrawal") carry a server-suggested note and
// reserve link; applying saves both through the normal annotation endpoint, after
// which the alert clears itself on the reload (linked recurring => ignored).
//
// Family management-fee suggestions may carry `suggested_applies`: one entry per
// transaction (possibly across accounts). Apply walks that list; single-tx
// alerts keep using transaction_id + suggested_links on the host account.
//
// accountNumber must come from the alert's own wrapper, never from a
// panel-level variable: in a family panel the alerts of several accounts are
// merged into one list.
const ApplySuggestionButton = ({ accountNumber, alert, onApplied }) => {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  const multiApplies =
    Array.isArray(alert.suggested_applies) && alert.suggested_applies.length > 0;
  const canApply =
    multiApplies ||
    (alert.transaction_id != null && (alert.suggested_links || []).length > 0);

  if (!canApply) return null;

  const handleClick = async () => {
    setWorking(true);
    setError('');
    try {
      if (multiApplies) {
        const touched = new Set();
        // Sequential saves keep the annotation store simple and surface the
        // first real failure instead of a partial Promise.all race.
        for (const item of alert.suggested_applies) {
          const acct = item.account_number || accountNumber;
          const txId = item.transaction_id;
          if (!acct || txId == null) continue;
          // eslint-disable-next-line no-await-in-loop
          await cashReservesService.saveTransactionNote(acct, txId, {
            note: item.suggested_note || alert.suggested_note || '',
            note_reason_category:
              item.suggested_reason_category ||
              alert.suggested_reason_category ||
              null,
            links: (item.suggested_links || alert.suggested_links || []).map((link) => ({
              reserve_id: link.reserve_id,
              allocated_amount: link.allocated_amount ?? null,
            })),
          });
          touched.add(acct);
        }
        if (onApplied) {
          const hosts = touched.size > 0 ? [...touched] : [accountNumber];
          for (const acct of hosts) {
            // eslint-disable-next-line no-await-in-loop
            await Promise.resolve(onApplied(acct));
          }
        }
      } else {
        await cashReservesService.saveTransactionNote(accountNumber, alert.transaction_id, {
          note: alert.suggested_note || '',
          note_reason_category: alert.suggested_reason_category || null,
          links: (alert.suggested_links || []).map((link) => ({
            reserve_id: link.reserve_id,
            allocated_amount: link.allocated_amount ?? null,
          })),
        });
        if (onApplied) onApplied(accountNumber);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to apply suggestion');
    } finally {
      setWorking(false);
    }
  };

  const applyCount = multiApplies ? alert.suggested_applies.length : 1;
  const title = multiApplies
    ? `Save the suggested note and reserve link on ${applyCount} transactions`
    : 'Save the suggested note and reserve link on this transaction';
  const label = working
    ? 'Applying…'
    : multiApplies
      ? `Apply all (${applyCount})`
      : 'Apply';

  return (
    <span className="d-inline-flex align-items-center gap-2 ms-2">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary py-0"
        onClick={handleClick}
        disabled={working}
        title={title}
      >
        <i className={`bi ${working ? 'bi-hourglass-split' : 'bi-magic'} me-1`}></i>
        {label}
      </button>
      {error && <span className="text-danger small">{error}</span>}
    </span>
  );
};

export default ApplySuggestionButton;
